import { useParams } from 'react-router-dom';

import { Button } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';

import useWizard from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/context/useWizard';
import { useGetMemberId, useGridView } from 'hooks';
import useGetWizardFormItems from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useGetWizardFormItems';

import {
  mapWizardFormItemsToProducts,
  hydrateProductsWithHierarchy,
  applyAnswersToProducts,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/wizardProduct';
import {
  applyValidationResult,
  propagateUpcErrorsToShipperItems,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/validation';

import { STEP_TYPES } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/config/stepConstants';
import { MODAL_TYPES } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/config/stepModalConstants';

const WizardFooter = ({ onCloseWizard, onValidateWizardForm }) => {
  const {
    state,
    currentStep,
    canContinue,
    handleContinue,
    handleBack,
    actions,
    handleGoToStep,
  } = useWizard();

  const { id } = useParams();

  const stepType = currentStep?.type;

  const { detailItems: selectedProducts } = useGridView();

  const { getWizardFormItems } = useGetWizardFormItems();

  const { memberId } = useGetMemberId();

  const isIntroStep = stepType === STEP_TYPES.INTRO;
  const isContactSale = stepType === STEP_TYPES.CONTACT_SALES;

  if (isIntroStep) {
    return (
      <div className='wizard-footer'>
        <Button onClick={onCloseWizard}>Cancel</Button>
      </div>
    );
  }

  if (stepType === STEP_TYPES.INPUT) {
    return (
      <div className='wizard-footer'>
        <Button onClick={onCloseWizard}>Cancel</Button>
        <div className='wizard-footer-right'>
          <Button onClick={handleBack} icon={<ArrowLeftOutlined />}>
            Go Back
          </Button>
          <Button
            type='primary'
            onClick={() => {
              handleContinue();
            }}
            disabled={!canContinue}
            icon={<ArrowRightOutlined />}
          >
            Continue
          </Button>
        </div>
      </div>
    );
  }

  if (stepType === STEP_TYPES.RECOMMENDATION_MORE_LESS_15_PRODUCTS) {
    const handleContinueToOnlineGrid = async () => {
      actions.openModal(MODAL_TYPES.ONLINE_GRID);
      actions.setGridLoading(true);

      try {
        const productIds = id
          ? [id]
          : selectedProducts.map((p) => p.id || p.productId);

        const responseWizardFormItems = await getWizardFormItems({
          productIds,
        });

        if (!responseWizardFormItems?.isSuccess) return;

        const wizardFormItems =
          responseWizardFormItems?.data?.wizardFormItems ?? [];

        const mappedProducts = mapWizardFormItemsToProducts(wizardFormItems);
        const hydratedProducts = hydrateProductsWithHierarchy(mappedProducts);
        const answeredProducts = applyAnswersToProducts(
          hydratedProducts,
          state.answers
        );

        const responseValidation = await onValidateWizardForm({
          products: answeredProducts,
          memberId,
        });

        let updatedProducts = applyValidationResult({
          response: responseValidation,
          products: answeredProducts,
        });

        updatedProducts = propagateUpcErrorsToShipperItems(updatedProducts);

        actions.setProducts(updatedProducts);
      } finally {
        actions.setGridLoading(false);
      }
    };

    return (
      <div className='wizard-footer'>
        <Button onClick={onCloseWizard}>Cancel</Button>
        <div className='wizard-footer-right'>
          <Button onClick={handleBack} icon={<ArrowLeftOutlined />}>
            Go Back
          </Button>
          <Button
            type='primary'
            onClick={handleContinueToOnlineGrid}
            icon={<ArrowRightOutlined />}
          >
            Continue
          </Button>
        </div>
      </div>
    );
  }

  if (stepType === STEP_TYPES.RECOMMENDATION_MORE_THAN_15_PRODUCTS) {
    return (
      <div className='wizard-footer'>
        <Button onClick={onCloseWizard}>Cancel</Button>
        <div className='wizard-footer-right'>
          <Button onClick={handleBack} icon={<ArrowLeftOutlined />}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  if (stepType === STEP_TYPES.PRODUCT_GRID_QUESTION) {
    return (
      <div className='wizard-footer'>
        <Button onClick={onCloseWizard}>Cancel</Button>
        <div className='wizard-footer-right'>
          <Button
            onClick={() => {
              actions.resetAnswers();
              handleGoToStep(STEP_TYPES.INTRO);
              actions.setBypassed(false);
            }}
            icon={<ArrowLeftOutlined />}
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  if (stepType === 'success') {
    // Success step - only Close button
    return (
      <div className='wizard-footer'>
        <Button type='primary' onClick={onCloseWizard}>
          Close
        </Button>
      </div>
    );
  }

  if (isContactSale) {
    return (
      <div className='wizard-footer'>
        <Button onClick={onCloseWizard}>Cancel</Button>
        <div className='wizard-footer-right'>
          <Button onClick={handleBack} icon={<ArrowLeftOutlined />}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='wizard-footer'>
      <Button onClick={onCloseWizard}>Cancel</Button>
      <div className='wizard-footer-right'>
        <Button onClick={handleBack} icon={<ArrowLeftOutlined />}>
          Go Back
        </Button>
        <Button
          type='primary'
          onClick={handleContinue}
          disabled={!canContinue}
          icon={<ArrowRightOutlined />}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default WizardFooter;
